import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';
import { Eye, FileDown, Loader2, ShoppingCart } from 'lucide-react';
import { PaginationControls } from '../PaginationControls';

const REVIEW_BADGE = {
  pending: ['Pending', 'bg-amber-100 text-amber-800'],
  draft: ['In progress', 'bg-amber-100 text-amber-800'],
  downloaded: ['Awaiting signed', 'bg-blue-100 text-blue-800'],
  signed_uploaded: ['Signed', 'bg-emerald-100 text-emerald-800'],
};

export function OrdersTable({
  orders, loading, total, page, totalPages, limit, onLimitChange,
  onPageChange, formatCurrency, formatDate, getStatusBadge,
  selectedIds, onToggleOne, onTogglePage, allOnPageSelected, reviewStatuses,
}) {
  const navigate = useNavigate();
  const selectionEnabled = typeof onToggleOne === 'function';

  return (
    <Card className="card-dashboard">
      <CardHeader>
        <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          Orders ({total})
          {selectionEnabled && selectedIds && selectedIds.size > 0 && (
            <Badge variant="secondary" className="ml-2" data-testid="orders-selected-count">
              {selectedIds.size} selected
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No orders found</p>
            <p className="text-sm">Create orders from quotations or use Direct Order</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {selectionEnabled && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allOnPageSelected}
                      onCheckedChange={onTogglePage}
                      aria-label="Select all on this page"
                      data-testid="orders-select-all-page"
                    />
                  </TableHead>
                )}
                <TableHead>Order #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Customer PO</TableHead>
                <TableHead>Quotation Ref</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Review sheet</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => {
                const isChecked = selectionEnabled && selectedIds?.has(order.id);
                return (
                  <TableRow key={order.id} data-state={isChecked ? 'selected' : undefined}>
                    {selectionEnabled && (
                      <TableCell>
                        <Checkbox
                          checked={!!isChecked}
                          onCheckedChange={() => onToggleOne(order.id)}
                          aria-label={`Select order ${order.order_number}`}
                          data-testid={`orders-row-checkbox-${order.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono font-semibold">{order.order_number}</TableCell>
                    <TableCell>
                      <Badge variant={order.order_type === 'direct' ? 'outline' : 'secondary'}>
                        {order.order_type === 'direct' ? 'Direct' : 'Quote'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{order.customer_code}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{order.customer_po_number || '-'}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">{order.quotation_number || '-'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(order.total_amount, order.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{order.items?.length || 0} items</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(order.status)}</TableCell>
                    <TableCell>
                      {(() => {
                        const review = reviewStatuses?.[order.id];
                        const [label, className] = REVIEW_BADGE[review?.status] || REVIEW_BADGE.pending;
                        return (
                          <button type="button" className="text-left"
                            onClick={() => navigate(`/orders/${order.id}/review-sheet`)}
                            data-testid={`order-review-badge-${order.id}`}>
                            <Badge className={className}>{label}</Badge>
                          </button>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(order.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/orders/${order.id}`)}
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`/orders/${order.id}/print`, '_blank')}
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <PaginationControls
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={onLimitChange}
          onPageChange={onPageChange}
        />
      </CardContent>
    </Card>
  );
}
